import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	DocumentTextIcon,
	MagnifyingGlassIcon,
	ExclamationTriangleIcon,
	PencilSquareIcon,
	CheckCircleIcon,
	ClockIcon,
	XCircleIcon,
} from '@heroicons/react/24/outline';
import AppLayout from '../../components/layout/AppLayout';
import { Alert, AppLoader, Button, Modal, Spinner } from '../../components/ui';
import { staffProfileApi, ApiError } from '../../api';
import type { StaffTcr, StaffTcrResponse } from '../../types/staffTcr';

// ─── Constants ──────────────────────────────────────────────────

const STATUS_STYLES: Record<
	string,
	{ border: string; badge: string; banner: string; icon: string }
> = {
	PENDING: {
		border: 'border-l-amber-500',
		badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
		banner: 'bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800',
		icon: 'bg-amber-100 dark:bg-amber-900/40',
	},
	APPROVED: {
		border: 'border-l-emerald-500',
		badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
		banner: 'bg-emerald-50 border border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800',
		icon: 'bg-emerald-100 dark:bg-emerald-900/40',
	},
	REJECTED: {
		border: 'border-l-red-500',
		badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
		banner: 'bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800',
		icon: 'bg-red-100 dark:bg-red-900/40',
	},
};

const STATUS_OPTIONS = [
	{ value: 'ALL', label: 'All Status' },
	{ value: 'PENDING', label: 'Pending' },
	{ value: 'APPROVED', label: 'Approved' },
	{ value: 'REJECTED', label: 'Rejected' },
];

const PERIOD_OPTIONS = [
	{ value: 'ALL', label: 'All Time' },
	{ value: 'TODAY', label: 'Today' },
	{ value: 'THIS_WEEK', label: 'This Week' },
	{ value: 'THIS_MONTH', label: 'This Month' },
];

// ─── Helpers ────────────────────────────────────────────────────

const formatCurrency = (amount: number): string => {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);
};

const formatDate = (dateStr: string): string => {
	try {
		const date = new Date(dateStr);
		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});
	} catch {
		return dateStr;
	}
};

const formatTaskType = (taskType: string): string => {
	return taskType.charAt(0).toUpperCase() + taskType.slice(1).toLowerCase();
};

const getDateParts = (dateStr: string): { day: string; month: string } => {
	try {
		const date = new Date(dateStr);
		return {
			day: date.getDate().toString(),
			month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
		};
	} catch {
		return { day: '-', month: '-' };
	}
};

const getStatusStyle = (status: string) => STATUS_STYLES[status] || STATUS_STYLES.PENDING;

const StatusIcon = ({ status, className }: { status: string; className?: string }) => {
	const iconClass = className || 'w-5 h-5';
	switch (status) {
		case 'APPROVED':
			return <CheckCircleIcon className={iconClass} />;
		case 'REJECTED':
			return <XCircleIcon className={iconClass} />;
		default:
			return <ClockIcon className={iconClass} />;
	}
};

// ─── Pagination Component ───────────────────────────────────────

interface PaginationProps {
	currentPage: number;
	totalPages: number;
	totalElements: number;
	pageSize: number;
	onPageChange: (page: number) => void;
}

const Pagination = ({
	currentPage,
	totalPages,
	totalElements,
	pageSize,
	onPageChange,
}: PaginationProps) => {
	const pages = useMemo(() => {
		const result: (number | 'dots')[] = [];
		if (totalPages <= 7) {
			for (let i = 0; i < totalPages; i++) result.push(i);
		} else {
			result.push(0);
			if (currentPage > 2) result.push('dots');
			for (
				let i = Math.max(1, currentPage - 1);
				i <= Math.min(totalPages - 2, currentPage + 1);
				i++
			) {
				result.push(i);
			}
			if (currentPage < totalPages - 3) result.push('dots');
			result.push(totalPages - 1);
		}
		return result;
	}, [currentPage, totalPages]);

	const start = currentPage * pageSize + 1;
	const end = Math.min((currentPage + 1) * pageSize, totalElements);

	if (totalPages <= 1) return null;

	return (
		<div className="flex items-center justify-center gap-2 mt-8 flex-wrap">
			<button
				onClick={() => onPageChange(0)}
				disabled={currentPage === 0}
				className="w-10 h-10 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-lg text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center hover:border-[#667eea] hover:text-[#667eea] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-200 dark:disabled:hover:border-gray-600 disabled:hover:text-gray-500 transition-colors"
			>
				&laquo;
			</button>
			<button
				onClick={() => onPageChange(currentPage - 1)}
				disabled={currentPage === 0}
				className="w-10 h-10 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-lg text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center hover:border-[#667eea] hover:text-[#667eea] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-200 dark:disabled:hover:border-gray-600 disabled:hover:text-gray-500 transition-colors"
			>
				&lsaquo;
			</button>
			<div className="flex items-center gap-1">
				{pages.map((page, idx) =>
					page === 'dots' ? (
						<span key={`dots-${idx}`} className="px-2 text-gray-400 dark:text-gray-500">
							...
						</span>
					) : (
						<button
							key={page}
							onClick={() => onPageChange(page)}
							className={`w-10 h-10 rounded-lg text-sm font-medium flex items-center justify-center transition-colors ${
								page === currentPage
									? 'bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white border border-transparent'
									: 'border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:border-[#667eea] hover:text-[#667eea]'
							}`}
						>
							{page + 1}
						</button>
					)
				)}
			</div>
			<button
				onClick={() => onPageChange(currentPage + 1)}
				disabled={currentPage === totalPages - 1}
				className="w-10 h-10 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-lg text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center hover:border-[#667eea] hover:text-[#667eea] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-200 dark:disabled:hover:border-gray-600 disabled:hover:text-gray-500 transition-colors"
			>
				&rsaquo;
			</button>
			<button
				onClick={() => onPageChange(totalPages - 1)}
				disabled={currentPage === totalPages - 1}
				className="w-10 h-10 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-lg text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center hover:border-[#667eea] hover:text-[#667eea] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-200 dark:disabled:hover:border-gray-600 disabled:hover:text-gray-500 transition-colors"
			>
				&raquo;
			</button>
			<span className="ml-4 text-sm text-gray-500 dark:text-gray-400">
				Showing {start}-{end} of {totalElements}
			</span>
		</div>
	);
};

// ─── TCR Detail Modal ───────────────────────────────────────────

interface TCRDetailModalProps {
	tcr: StaffTcr | null;
	onClose: () => void;
}

const TCRDetailModal = ({ tcr, onClose }: TCRDetailModalProps) => {
	const navigate = useNavigate();

	if (!tcr) return null;

	const style = getStatusStyle(tcr.status);
	const showEdit = tcr.editable && tcr.status !== 'APPROVED';

	const headerContent = (
		<div
			className="px-6 py-5 flex items-center gap-4"
			style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
		>
			<div className="flex-1 min-w-0">
				<h2 className="text-lg font-bold text-white truncate">Task Completion Report</h2>
				<p className="text-sm text-white/80 mt-0.5 truncate">
					{tcr.serviceName} &middot; {formatDate(tcr.appointmentDateTime)}
				</p>
			</div>
		</div>
	);

	return (
		<Modal
			isOpen={true}
			onClose={onClose}
			title=""
			size="xl"
			headerContent={headerContent}
			panelClassName="max-h-[90vh] flex flex-col"
			bodyClassName="!p-0 flex-1 flex flex-col min-h-0"
		>
			<div className="flex-1 overflow-y-auto p-6 space-y-6">
				{/* Status Banner */}
				<div className={`flex items-center gap-3 p-4 rounded-xl ${style.banner}`}>
					<div
						className={`w-10 h-10 rounded-lg flex items-center justify-center ${style.icon}`}
					>
						<StatusIcon status={tcr.status} className="w-5 h-5" />
					</div>
					<div>
						<p className="font-semibold text-gray-900 dark:text-white">
							{tcr.status === 'APPROVED'
								? 'Approved'
								: tcr.status === 'REJECTED'
									? 'Rejected'
									: 'Pending Review'}
						</p>
						<p className="text-sm text-gray-500 dark:text-gray-400">
							Submitted {formatDate(tcr.submittedAt)}
						</p>
					</div>
				</div>

				{/* Service Details */}
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
						Service Details
					</h3>
					<div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
						{[
							{ label: 'Client', value: tcr.customerName },
							{ label: 'Service', value: tcr.serviceName },
							{ label: 'Size', value: tcr.size || '-' },
							{ label: 'Length', value: tcr.hairLength || '-' },
							{ label: 'Date', value: formatDate(tcr.appointmentDateTime) },
							{ label: 'Service Price', value: formatCurrency(tcr.amount) },
						].map(item => (
							<div
								key={item.label}
								className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg"
							>
								<p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
									{item.label}
								</p>
								<p className="text-sm font-semibold text-gray-900 dark:text-white">
									{item.value}
								</p>
							</div>
						))}
					</div>
				</div>

				{/* Your Assigned Tasks */}
				{tcr.taskCompletions && tcr.taskCompletions.length > 0 && (
					<div>
						<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
							Your Assigned Tasks
						</h3>
						<div className="space-y-3">
							{tcr.taskCompletions.map((task, idx) => {
								const pctDisplay = Math.round(task.completionPct * 100);
								return (
									<div
										key={idx}
										className="flex items-center gap-3 bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg"
									>
										<div
											className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold text-sm"
											style={{
												background:
													'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
											}}
										>
											{task.taskType.charAt(0)}
										</div>
										<div>
											<p className="font-semibold text-gray-900 dark:text-white">
												{formatTaskType(task.taskType)}
											</p>
											<p className="text-sm text-gray-500 dark:text-gray-400">
												{pctDisplay}% completion
											</p>
										</div>
									</div>
								);
							})}
						</div>
					</div>
				)}

				{/* Materials Used */}
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
						Materials Used
					</h3>
					{tcr.materials && tcr.materials.length > 0 ? (
						<div className="space-y-2">
							{tcr.materials.map((material, idx) => (
								<div
									key={idx}
									className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg"
								>
									<div>
										<p className="text-sm font-medium text-gray-900 dark:text-white">
											{material.materialName ||
												formatTaskType(material.materialType)}
										</p>
										{material.length && (
											<p className="text-xs text-gray-500 dark:text-gray-400">
												Length: {material.length}
											</p>
										)}
									</div>
									<div className="text-right">
										<p className="text-sm font-medium text-gray-900 dark:text-white">
											{material.packs} pack{material.packs !== 1 ? 's' : ''}
										</p>
										<p className="text-xs text-gray-500 dark:text-gray-400">
											{formatCurrency(material.price)}
										</p>
									</div>
								</div>
							))}
						</div>
					) : (
						<p className="text-sm text-gray-400 dark:text-gray-500 italic">
							No materials used
						</p>
					)}
				</div>

				{/* Tip */}
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
						Tip from Customer
					</h3>
					{tcr.tip > 0 ? (
						<div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-4 rounded-lg">
							<span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
								{formatCurrency(tcr.tip)}
							</span>
						</div>
					) : (
						<p className="text-sm text-gray-400 dark:text-gray-500 italic">
							No tip received
						</p>
					)}
				</div>

				{/* Work Notes */}
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
						Work Notes
					</h3>
					{tcr.notes ? (
						<div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border-l-3 border-[#667eea]">
							<p className="text-sm text-gray-700 dark:text-gray-300">{tcr.notes}</p>
						</div>
					) : (
						<p className="text-sm text-gray-400 dark:text-gray-500 italic">
							No work notes
						</p>
					)}
				</div>

				{/* Photos */}
				{tcr.workPhotosBase64 && tcr.workPhotosBase64.length > 0 && (
					<div>
						<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
							Completed Work Photos
						</h3>
						<div className="grid grid-cols-2 gap-3">
							{tcr.workPhotosBase64.map((photo, idx) => (
								<div
									key={idx}
									className="rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 aspect-square"
								>
									<img
										src={
											photo.startsWith('data:')
												? photo
												: `data:image/jpeg;base64,${photo}`
										}
										alt={`Work photo ${idx + 1}`}
										className="w-full h-full object-cover"
									/>
								</div>
							))}
						</div>
					</div>
				)}

				{/* Cost Summary */}
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
						Cost Summary
					</h3>
					<div className="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 p-5">
						{/* Service Price */}
						<div className="flex justify-between items-center py-2.5">
							<span className="text-[0.95rem] text-gray-500 dark:text-gray-400">
								Service Price
							</span>
							<span className="font-semibold text-gray-900 dark:text-gray-100">
								{formatCurrency(tcr.amount)}
							</span>
						</div>

						{/* Materials Cost */}
						<div className="text-[0.95rem] font-semibold text-emerald-500 dark:text-emerald-400 mt-4 mb-1 pb-2 border-b border-gray-200 dark:border-gray-600">
							Materials Cost
						</div>
						{tcr.materials && tcr.materials.length > 0 ? (
							<>
								{(tcr.materials ?? [])
									.filter(m => !m.skipCalculation)
									.map((m, idx) => (
										<div
											key={idx}
											className="flex justify-between items-center py-1.5 pl-4"
										>
											<span className="text-[0.9rem] text-gray-500 dark:text-gray-400">
												{m.materialName || formatTaskType(m.materialType)}
											</span>
											<span className="font-semibold text-gray-900 dark:text-gray-100">
												{formatCurrency(m.price)}
											</span>
										</div>
									))}
								<div className="flex justify-between items-center pt-2 mt-1 pl-4 border-t border-gray-200 dark:border-gray-600">
									<span className="font-semibold text-gray-700 dark:text-gray-200">
										Total Materials
									</span>
									<span className="font-semibold text-gray-900 dark:text-gray-100">
										{formatCurrency(tcr.materialsCost)}
									</span>
								</div>
							</>
						) : (
							<p className="pl-4 py-1.5 text-gray-400 dark:text-gray-500">-</p>
						)}

						{/* Net Service Price */}
						<div className="text-[0.95rem] font-semibold text-emerald-500 dark:text-emerald-400 mt-4 mb-1 pb-2 border-b border-gray-200 dark:border-gray-600">
							Net Service Price
						</div>
						<div className="flex justify-between items-center py-1.5 pl-4">
							<span className="text-[0.9rem] text-gray-500 dark:text-gray-400">
								{formatCurrency(tcr.amount)} &minus; {formatCurrency(tcr.materialsCost)}
							</span>
							<span className="font-semibold text-gray-900 dark:text-gray-100">
								{formatCurrency(tcr.netServicePrice)}
							</span>
						</div>

						{/* Commission */}
						<div className="text-[0.95rem] font-semibold text-emerald-500 dark:text-emerald-400 mt-4 mb-1 pb-2 border-b border-gray-200 dark:border-gray-600">
							Your Commission
						</div>
						<div className="flex justify-between items-center py-1.5 pl-4">
							<span className="text-[0.9rem] text-gray-500 dark:text-gray-400">
								{formatCurrency(tcr.netServicePrice)} &times; {tcr.commissionRate}%
							</span>
							<span className="font-semibold text-emerald-500 dark:text-emerald-400">
								{formatCurrency(tcr.commission)}
							</span>
						</div>
						{tcr.taskCompletions && tcr.taskCompletions.length > 0 && (
							<div className="pl-4 mt-1">
								{tcr.taskCompletions.map((task, idx) => (
									<p key={idx} className="text-xs text-gray-400 dark:text-gray-500">
										{formatTaskType(task.taskType)}: {Math.round(task.completionPct * 100)}% completion
									</p>
								))}
							</div>
						)}

						{/* Customer Tip */}
						<div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
							<span className="text-[0.95rem] font-semibold text-emerald-500 dark:text-emerald-400">
								Customer Tip
							</span>
							<span className="font-semibold text-gray-900 dark:text-gray-100">
								{formatCurrency(tcr.tip)}
							</span>
						</div>

						{/* Total Earnings Box */}
						<div className="bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-900 dark:to-emerald-800 rounded-xl p-5 text-center mt-4 border-2 border-emerald-300 dark:border-emerald-600">
							<p className="font-semibold text-emerald-700 dark:text-emerald-300 mb-2">
								Your Total Earnings
							</p>
							<div className="flex justify-center gap-6 text-[0.9rem] text-emerald-600 dark:text-emerald-400 mb-2">
								<span>Commission: {formatCurrency(tcr.commission)}</span>
								<span>Tip: {formatCurrency(tcr.tip)}</span>
							</div>
							<p className="text-3xl font-bold text-emerald-500 dark:text-emerald-400">
								{formatCurrency(tcr.totalEarningsAmount)}
							</p>
						</div>
					</div>
				</div>

				{/* Review Notes (rejected only) */}
				{tcr.status === 'REJECTED' && tcr.reviewNotes && (
					<div>
						<h3 className="text-sm font-semibold text-red-700 dark:text-red-400 mb-3 flex items-center gap-2">
							<ExclamationTriangleIcon className="w-4 h-4" />
							Review Notes
						</h3>
						<div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-lg">
							<p className="text-sm text-red-700 dark:text-red-300">
								{tcr.reviewNotes}
							</p>
						</div>
					</div>
				)}
			</div>

			{/* Footer */}
			{showEdit && (
				<div className="border-t border-gray-200 dark:border-gray-700 p-4 flex justify-end">
					<Button
						variant="primary"
						onClick={() => {
							onClose();
							navigate(`/staff/tcr/${tcr.id}/edit`);
						}}
					>
						<PencilSquareIcon className="w-4 h-4 mr-2" />
						Edit & Resubmit TCR
					</Button>
				</div>
			)}
		</Modal>
	);
};

// ─── Main TCR Page ──────────────────────────────────────────────

const StaffTCR = () => {
	const navigate = useNavigate();

	// Filter state
	const [statusFilter, setStatusFilter] = useState('ALL');
	const [periodFilter, setPeriodFilter] = useState('ALL');
	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [currentPage, setCurrentPage] = useState(0);

	// Data state
	const [data, setData] = useState<StaffTcrResponse | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Modal state
	const [selectedTcr, setSelectedTcr] = useState<StaffTcr | null>(null);

	// Debounce search
	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search), 300);
		return () => clearTimeout(timer);
	}, [search]);

	// Reset page when filters change
	useEffect(() => {
		setCurrentPage(0);
	}, [statusFilter, periodFilter, debouncedSearch]);

	// Fetch TCRs
	const fetchTcrs = useCallback(async () => {
		setIsLoading(true);
		setError(null);
		try {
			const response = await staffProfileApi.getTcrs({
				status: statusFilter,
				search: debouncedSearch || undefined,
				period: periodFilter,
				page: currentPage,
				size: 20,
			});
			setData(response);
		} catch (err) {
			const apiError = err as ApiError;
			setError(apiError.message || 'Failed to load task completion reports');
		} finally {
			setIsLoading(false);
		}
	}, [statusFilter, periodFilter, debouncedSearch, currentPage]);

	useEffect(() => {
		fetchTcrs();
	}, [fetchTcrs]);

	const tcrs = data?.tcrs ?? [];

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-900 dark:text-white">
					Task Completion Reports
				</h1>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
					View all your submitted task completion reports
				</p>
			</div>

			{/* Stats Banner */}
			{data && (
				<div
					className="rounded-2xl px-7 py-5 mb-6 flex items-center justify-between flex-wrap gap-5"
					style={{
						background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
						boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
					}}
				>
					<div className="flex items-center gap-4">
						<div className="w-[60px] h-[60px] rounded-xl bg-white/20 flex items-center justify-center">
							<DocumentTextIcon className="w-8 h-8 text-white" />
						</div>
						<div>
							<p className="text-3xl font-bold text-white leading-none">
								{data.totalSubmitted}
							</p>
							<p className="text-sm text-white/80 mt-1">Total TCRs Submitted</p>
						</div>
					</div>
					<div className="flex gap-8">
						<div className="text-center">
							<p className="text-xl font-bold text-white">{data.approvedCount}</p>
							<p className="text-xs text-white/70">Approved</p>
						</div>
						<div className="text-center">
							<p className="text-xl font-bold text-white">{data.pendingCount}</p>
							<p className="text-xs text-white/70">Pending</p>
						</div>
						<div className="text-center">
							<p className="text-xl font-bold text-white">
								{formatCurrency(data.totalEarnings)}
							</p>
							<p className="text-xs text-white/70">Total Earnings</p>
						</div>
					</div>
				</div>
			)}

			{/* Filter Bar */}
			<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 mb-6 flex flex-wrap gap-3 items-center">
				<div className="relative flex-1 min-w-[200px]">
					<MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
					<input
						type="text"
						placeholder="Search by client or service..."
						value={search}
						onChange={e => setSearch(e.target.value)}
						className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-[#667eea] transition-colors"
					/>
				</div>
				<select
					value={statusFilter}
					onChange={e => setStatusFilter(e.target.value)}
					className="px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] cursor-pointer min-w-[150px]"
				>
					{STATUS_OPTIONS.map(opt => (
						<option key={opt.value} value={opt.value}>
							{opt.label}
						</option>
					))}
				</select>
				<select
					value={periodFilter}
					onChange={e => setPeriodFilter(e.target.value)}
					className="px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] cursor-pointer min-w-[150px]"
				>
					{PERIOD_OPTIONS.map(opt => (
						<option key={opt.value} value={opt.value}>
							{opt.label}
						</option>
					))}
				</select>
			</div>

			{/* Content */}
			{isLoading && !data ? (
				<AppLoader message="Loading reports" variant="inline" />
			) : error ? (
				<Alert
					type="warning"
					icon={<ExclamationTriangleIcon className="w-8 h-8" />}
					title="Unable to Load Reports"
					message={error}
					actions={[{ label: 'Retry', onClick: fetchTcrs, primary: true }]}
					onDismiss={() => setError(null)}
				/>
			) : !isLoading && tcrs.length === 0 ? (
				<div className="text-center py-16">
					<DocumentTextIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
					<h3 className="text-lg font-semibold text-gray-500 dark:text-gray-400 mb-1">
						No reports found
					</h3>
					<p className="text-sm text-gray-400 dark:text-gray-500">
						{debouncedSearch || statusFilter !== 'ALL' || periodFilter !== 'ALL'
							? 'Try adjusting your search or filters'
							: 'Your task completion reports will appear here'}
					</p>
				</div>
			) : (
				<>
					{/* Loading indicator for filter/page changes */}
					{isLoading && (
						<div className="flex justify-center py-4">
							<Spinner size="md" className="text-[#667eea]" />
						</div>
					)}

					{/* TCR Cards */}
					<div className="flex flex-col gap-4">
						{tcrs.map(tcr => {
							const dateParts = getDateParts(tcr.appointmentDateTime);
							const style = getStatusStyle(tcr.status);
							const showEdit = tcr.editable && tcr.status !== 'APPROVED';

							return (
								<div
									key={tcr.id}
									onClick={() => setSelectedTcr(tcr)}
									className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 flex items-center gap-4 cursor-pointer border-l-4 ${style.border} hover:shadow-md hover:-translate-y-0.5 transition-all`}
								>
									{/* Date Block */}
									<div className="text-center min-w-[60px] py-2 px-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hidden sm:block">
										<p className="text-xl font-bold text-[#667eea] leading-none">
											{dateParts.day}
										</p>
										<p className="text-xs text-gray-500 dark:text-gray-400 uppercase mt-1">
											{dateParts.month}
										</p>
									</div>

									{/* Info */}
									<div className="flex-1 min-w-0">
										<p className="font-semibold text-gray-900 dark:text-white truncate">
											{tcr.customerName}
										</p>
										<p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
											{tcr.serviceName}
											{tcr.size ? ` \u00B7 ${tcr.size}` : ''}
											{tcr.hairLength ? ` \u00B7 ${tcr.hairLength}` : ''}
										</p>
										{tcr.selectedTasks.length > 0 && (
											<div className="flex flex-wrap gap-1.5 mt-2">
												{tcr.selectedTasks.map(task => (
													<span
														key={task.serviceTaskId}
														className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white"
													>
														{formatTaskType(task.taskType)}
													</span>
												))}
											</div>
										)}
									</div>

									{/* Right */}
									<div className="text-right min-w-[110px] flex-shrink-0">
										<p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
											{formatCurrency(tcr.totalEarningsAmount)}
										</p>
										<span
											className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${style.badge}`}
										>
											{tcr.status}
										</span>
										<p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
											Submitted {formatDate(tcr.submittedAt)}
										</p>
										{showEdit && (
											<button
												onClick={e => {
													e.stopPropagation();
													navigate(`/staff/tcr/${tcr.id}/edit`);
												}}
												className="mt-2 px-3 py-1.5 rounded-md text-xs font-medium text-white"
												style={{
													background:
														'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
												}}
											>
												<PencilSquareIcon className="w-3 h-3 inline mr-1" />
												Edit
											</button>
										)}
									</div>
								</div>
							);
						})}
					</div>

					{/* Pagination */}
					{data && data.totalPages > 1 && (
						<Pagination
							currentPage={data.page}
							totalPages={data.totalPages}
							totalElements={data.totalElements}
							pageSize={data.size}
							onPageChange={setCurrentPage}
						/>
					)}
				</>
			)}

			{/* Detail Modal */}
			<TCRDetailModal tcr={selectedTcr} onClose={() => setSelectedTcr(null)} />
		</AppLayout>
	);
};

export default StaffTCR;
